import {
  TextField, Dialog, Checkbox, FormControlLabel, Tooltip,
  InputLabel, Select, MenuItem, SelectChangeEvent,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { User } from 'lib/types/users';
import { useState, ChangeEvent, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import {
  userIsProOrAdmin, userIsTeamMember, userViewableTeams, buildJobPath,
} from 'lib/helpers';
import { Job } from 'lib/types/jobs';

interface Props {
  profileUser: User;
  onCancel: () => void;
}

const defaultSubject = 'I saw your profile on Gondola';

export const ContactModal = ({
  profileUser, onCancel,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState('');
  const [ccEmail, setCcEmail] = useState(true);
  const [jobInvite, setJobInvite] = useState<Job>();
  const [jobs, setJobs] = useState<Job[]>([]);

  const disableSubject = !userIsProOrAdmin(currentUser) && !userIsTeamMember(currentUser);

  const { enqueueSnackbar } = useSnackbar();

  const change = (e: ChangeEvent<HTMLInputElement>) => {
    setBody(e.target.value);
  };

  const sendEmail = async () => {
    try {
      await API.sendDirectMessage(profileUser.id, subject, body, ccEmail);
      enqueueSnackbar(`Sent your message to ${profileUser.name}`, {
        variant: 'success',
      });
      onCancel();
    } catch (e: any) {
      enqueueSnackbar(`Failed to send your message to ${profileUser.name}: ${e}`, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (jobInvite) {
      setBody(`Hi, ${profileUser.name}!
We think you would be a great fit for this role: ${jobInvite?.name}. Let me know if you're interested, or just apply at the link below. Thanks!
${currentUser?.name}

https://gondola.cc${buildJobPath(jobInvite)}`);
      setSubject(`Apply to ${jobInvite?.name} on Gondola!`);
    } else {
      setBody('');
      setSubject(defaultSubject);
    }
  }, [jobInvite]);

  const teamId = userViewableTeams(currentUser)?.[0]?.id;
  const {
    error, data,
  } = useQuery({
    enabled: userIsTeamMember(currentUser) && !!teamId,
    queryKey: ['getTeamJobs', {
      teamId, status: 'open', visibility: 'all', currentUserId: currentUser?.id,
    }],
    queryFn: async () => {
      if (!teamId) return null;
      return API.getTeamJobs(teamId, { status: 'open', visibility: 'all' });
    },
  });

  useEffect(() => {
    if (data) {
      setJobs(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error fetching jobs: ${error}`, { variant: 'error' });
    }
  }, [error]);

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <div className="p-6">
        <h1 className="text-lg font-bold mb-4">
          Send a message to
          {' '}
          {profileUser.name}
        </h1>
        <div>
          <div className="mb-4 flex items-center gap-2">
            <TextField
              variant="outlined"
              name="subject"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              disabled={disableSubject}
            />
            {disableSubject && (
              <Tooltip title="Upgrade your account to customize the subject of your messages">
                <Info color="secondary" />
              </Tooltip>
            )}
          </div>
          <div className="mb-4">
            <TextField
              variant="outlined"
              name="body"
              label={body.length ? '' : 'Message'}
              fullWidth
              multiline
              value={body}
              onChange={change}
              minRows={3}
            />
          </div>
          <div className="mb-4">
            <FormControlLabel
              control={(
                <Checkbox
                  id="send-copy"
                  name="send-copy"
                  checked={ccEmail}
                  onChange={() => setCcEmail(!ccEmail)}
                />
              )}
              label="Send me a copy"
            />
          </div>
          {jobs.length > 0 && (
            <div className="mb-4">
              <InputLabel id="team-label" className="font-semibold">Invite to apply to an open job</InputLabel>
              <Select
                labelId="jobs-label"
                fullWidth
                id="job-select"
                name="job-select"
                onChange={(e: SelectChangeEvent<string>) => {
                  const jobId = e.target.value;
                  if (!jobId) {
                    setJobInvite(undefined);
                    return;
                  }
                  const selectedJob = jobs.find((job) => job.id === Number(jobId));
                  setJobInvite(selectedJob);
                }}
                size="small"
                value={jobInvite?.id.toString() || ''}
              >
                <MenuItem value="">---</MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job.id} value={job.id}>{job.name}</MenuItem>
                ))}
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-primary-outlined" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={sendEmail}
            disabled={!body.length}
          >
            Send
          </button>
        </div>
      </div>
    </Dialog>
  );
};
